import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios, { api } from '../../utils/requestInterceptor';
import ProjectGrid from '../projects/ProjectGrid';
import StepDetailsModal from './StepDetailsModal';
import ClientPortalNotes from './ClientPortalNotes';
import ProjectFileUploader from './ProjectFileUploader';
import ProjectHeader from './ProjectHeader';
import DueDatesSummary from './DueDatesSummary';
import EmailVerification from './EmailVerification';
import ClientPortalMissingItems from './ClientPortalMissingItems';
import ClientPortalProjectPlan from './ClientPortalProjectPlan';

// File uploader component for clients
import ClientFileUploader from './ClientFileUploader';

function ClientPortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [phaseStatusTypes, setPhaseStatusTypes] = useState([]);
  const [stepStatusTypes, setStepStatusTypes] = useState([]);
  
  useEffect(() => {
    const initialize = async () => {
      console.log('Initializing client portal with token:', token);
      await validateAccess();
      fetchStatusTypes(); // Add status types fetching here
    };
    
    initialize();
  }, [token]);
  
  const validateAccess = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Validating access token (unencoded):', token);
      console.log('Validating access token (encoded):', encodeURIComponent(token));
      
      // Use the api instance which has the /api prefix
      const apiUrl = `/access/validate/${encodeURIComponent(token)}`;
      console.log('Making API call to:', apiUrl);
      
      // Validate the access token with the API instance
      const response = await api.get(apiUrl);
      
      console.log('Validation response:', response.data);
      
      if (response.data.requiresVerification) {
        console.log('Email verification required');
        setRequiresVerification(true);
        setEmail(response.data.email || '');
        setLoading(false);
        return;
      }
      
      console.log('Access token is valid, fetching project details');
      
      // If no verification required, fetch project details
      await fetchProjectDetails(response.data.projectId);
    } catch (error) {
      console.error('Error validating access:', error);
      console.error('Error response:', error.response?.data);
      setError('Invalid or expired access link.');
      setLoading(false);
    }
  };
  
  const fetchProjectDetails = async (projectId) => {
    try {
      setLoading(true);
      
      console.log('Fetching project details with token (unencoded):', token);
      console.log('Fetching project details with token (encoded):', encodeURIComponent(token));
      
      // Use the api instance with the correct path
      const apiUrl = `/access/client-project/${encodeURIComponent(token)}`;
      console.log('Making API call to:', apiUrl);
      
      // Use the client portal specific endpoint with the API instance
      const response = await api.get(apiUrl);
      
      console.log('Project details response:', response.data);
      
      if (response.data && response.data.project) {
        setProject(response.data.project);
        console.log('Project data loaded:', response.data.project);
      } else {
        console.error('Invalid project data format received:', response.data);
        setError('Failed to load project details. Invalid data format received.');
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      console.error('Error response:', error.response?.data);
      
      // Set a more detailed error message
      let errorMessage = 'Failed to load project details. ';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        errorMessage += error.response.data?.error || error.response.data?.message || error.message;
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage += 'The server did not respond. Please try again later.';
      } else {
        // Something happened in setting up the request
        errorMessage += error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyEmail = async () => {
    try {
      setLoading(true);
      
      console.log('Verifying email for token:', token);
      console.log('Email being verified:', email);
      
      // Use the api instance with the correct path
      const apiUrl = `/access/verify-email/${encodeURIComponent(token)}`;
      console.log('Making API call to:', apiUrl);
      
      const response = await api.post(apiUrl, { email });
      console.log('Email verification response:', response.data);
      
      if (response.data.verified) {
        // Fetch project details after verification
        await fetchProjectDetails(response.data.projectId);
        setRequiresVerification(false);
      }
    } catch (error) {
      console.error('Email verification error:', error);
      console.error('Error response:', error.response?.data);
      setError('Email verification failed. Please check your email and try again.');
      setLoading(false);
    }
  };
  
  const handleStepClick = (step) => {
    // If step already has notes and files arrays, use them directly
    if (step && step.id) {
      console.log('Opening step details:', step);
      setSelectedStep(step);
    } else {
      console.warn('Cannot open step details for step without ID');
    }
  };
  
  const handleFileUploaded = (file) => {
    // Update the files in the selected step
    if (selectedStep) {
      const updatedStep = {
        ...selectedStep,
        files: [file, ...(selectedStep.files || [])]
      };
      setSelectedStep(updatedStep);
      
      // Also update the step in the project data
      if (project) {
        const updatedPhases = project.phases.map(phase => {
          if (phase.id === selectedStep.phase_id) {
            const updatedSteps = phase.steps.map(step => {
              if (step.id === selectedStep.id) {
                return {
                  ...step,
                  files: [file, ...(step.files || [])]
                };
              }
              return step;
            });
            return { ...phase, steps: updatedSteps };
          }
          return phase;
        });
        
        setProject({ ...project, phases: updatedPhases });
      }
    }
  };
  
  const fetchStatusTypes = async () => {
    try {
      // Get phase status types
      console.log('Fetching phase status types');
      const phaseUrl = '/settings/status-types/phase';
      console.log('Making API call to:', phaseUrl);
      
      const phaseResponse = await api.get(phaseUrl);
      if (phaseResponse.data && phaseResponse.data.statusTypes) {
        setPhaseStatusTypes(phaseResponse.data.statusTypes);
        console.log('Phase status types loaded:', phaseResponse.data.statusTypes);
      }
      
      // Get step status types
      console.log('Fetching step status types');
      const stepUrl = '/settings/status-types/step';
      console.log('Making API call to:', stepUrl);
      
      const stepResponse = await api.get(stepUrl);
      if (stepResponse.data && stepResponse.data.statusTypes) {
        setStepStatusTypes(stepResponse.data.statusTypes);
        console.log('Step status types loaded:', stepResponse.data.statusTypes);
      }
    } catch (error) {
      console.error('Error fetching status types:', error);
      console.error('Status types error response:', error.response?.data);
      console.error('Status types error status:', error.response?.status);
      console.error('Status types error headers:', error.response?.headers);
      // Non-critical error, don't need to show to user
    }
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading project...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md p-8 space-y-4 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-medium text-gray-900">Access Error</h2>
            <p className="mt-2 text-sm text-gray-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (requiresVerification) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-medium text-gray-900">Email Verification Required</h2>
            <p className="mt-2 text-sm text-gray-500">
              Please verify your email to access the project.
            </p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter your email address"
              />
            </div>
            
            <button
              type="button"
              onClick={handleVerifyEmail}
              className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Verify Email
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Project header */}
      <ProjectHeader 
        project={project}
        loading={loading}
        error={error}
      />
      
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Due Dates Summary */}
        {!loading && !error && project && (
          <DueDatesSummary project={project} />
        )}
        
        {/* Project information */}
        <header className="bg-white shadow">
          <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-900">
                  {project.name}
                </h1>
                <span className="inline-flex items-center ml-4 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Client Portal
                </span>
              </div>

              <div className="mt-4 md:mt-0 flex flex-col sm:flex-row items-start sm:items-center">
                <div className="flex items-center bg-gray-100 px-3 py-1 rounded-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="font-medium text-gray-700">{project.client_name}</span>
                </div>
                
                <span className="hidden sm:inline-block mx-2 text-gray-400">•</span>
                
                {project.start_date && (
                  <>
                    <div className="mt-2 sm:mt-0 inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 border border-green-200 shadow-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Available: <strong>{new Date(project.start_date).toLocaleDateString()}</strong></span>
                    </div>
                    <span className="hidden sm:inline-block mx-2 text-gray-400">•</span>
                  </>
                )}

                {project.due_date ? (
                  <div className="mt-2 sm:mt-0 inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200 shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Due: <strong>{new Date(project.due_date).toLocaleDateString()}</strong></span>
                  </div>
                ) : (
                  <div className="mt-2 sm:mt-0 inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    No due date set
                  </div>
                )}
              </div>
            </div>
            {project.description && (
              <p className="mt-2 text-sm text-gray-600">{project.description}</p>
            )}
          </div>
        </header>
        
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          {/* Main content area with phases and steps */}
          <div className="px-4 py-6 sm:px-0">
            {/* Project-level file uploader */}
            <ProjectFileUploader 
              projectId={project.id} 
              token={token} 
              onFileUploaded={(file) => console.log('File uploaded:', file)} 
            />
            
            {/* Project plans section */}
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Plans</h2>
              <ClientPortalProjectPlan 
                projectId={project.id} 
                clientId={project.client_id} 
                token={token}
              />
            </div>
            
            {/* Project phases and steps */}
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Project Timeline</h2>
              <ProjectGrid 
                phases={project.phases} 
                handleStepClick={handleStepClick}
                phaseStatusTypes={phaseStatusTypes}
                stepStatusTypes={stepStatusTypes}
                handlePhaseClick={(phaseId) => {}}
                readOnly={true}
              />
            </div>
            
            {/* Notes section */}
            <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <ClientPortalNotes project={project} />
              </div>
            </div>
            
            {/* Missing Items section */}
            <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Information Needed</h3>
                <ClientPortalMissingItems project={project} token={token} />
              </div>
            </div>
          </div>
        </main>
        
        {/* Step Details Modal */}
        {selectedStep && (
          <StepDetailsModal 
            step={selectedStep} 
            token={token} 
            onClose={() => setSelectedStep(null)} 
            onFileUploaded={handleFileUploaded} 
          />
        )}
      </div>
    </div>
  );
}

export default ClientPortal; 